import { MAX_EXPERTS } from "../constants/experts";

const PREFS_KEY = "debate-wizard-prefs-v1";
const DRAFT_KEY = "debate-wizard-draft-v1";

export interface DebatePrefs {
  plan: "free" | "plus" | "pro";
  concise: boolean;
  audioMode: boolean;
  debateMode: "single" | "multi";
  autoExperts: boolean;
  experts: string[];
}

export interface DebateDraft {
  question: string;
  images: string[];
}

export const DEFAULT_DEBATE_PREFS: DebatePrefs = {
  plan: "free",
  concise: true,
  audioMode: false,
  debateMode: "multi",
  autoExperts: true,
  experts: [],
};

function readJson(key: string): Record<string, unknown> | null {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

export function loadDebatePrefs(): DebatePrefs {
  const saved = readJson(PREFS_KEY);
  if (!saved) return DEFAULT_DEBATE_PREFS;
  const d = DEFAULT_DEBATE_PREFS;
  return {
    plan:
      saved.plan === "free" || saved.plan === "plus" || saved.plan === "pro"
        ? saved.plan
        : d.plan,
    concise: typeof saved.concise === "boolean" ? saved.concise : d.concise,
    audioMode: typeof saved.audioMode === "boolean" ? saved.audioMode : d.audioMode,
    debateMode:
      saved.debateMode === "single" || saved.debateMode === "multi"
        ? saved.debateMode
        : d.debateMode,
    autoExperts:
      typeof saved.autoExperts === "boolean" ? saved.autoExperts : d.autoExperts,
    experts: Array.isArray(saved.experts)
      ? saved.experts.filter((e): e is string => typeof e === "string").slice(0, MAX_EXPERTS)
      : d.experts,
  };
}

export function saveDebatePrefs(prefs: DebatePrefs) {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* storage unavailable or full; preferences just won't persist */
  }
}

export function loadDebateDraft(): DebateDraft {
  const saved = readJson(DRAFT_KEY);
  if (!saved) return { question: "", images: [] };
  return {
    question: typeof saved.question === "string" ? saved.question : "",
    images: Array.isArray(saved.images)
      ? saved.images.filter((i): i is string => typeof i === "string")
      : [],
  };
}

export function saveDebateDraft(draft: DebateDraft) {
  try {
    if (!draft.question && draft.images.length === 0) {
      window.localStorage.removeItem(DRAFT_KEY);
      return;
    }
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Photos can exceed the storage quota; keep the text so typing is never lost.
    try {
      window.localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ question: draft.question, images: [] }),
      );
    } catch {
      /* storage unavailable */
    }
  }
}

export function clearDebateDraft() {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* storage unavailable */
  }
}
